/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HashtagPlugin } from "@lexical/react/LexicalHashtagPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useEffect, useRef, useState } from "react";
import { CAN_USE_DOM } from "./shared/canUseDOM";
import { $generateHtmlFromNodes } from "@lexical/html";
import {
	SharedHistoryContext,
	useSharedHistoryContext,
} from "./context/SharedHistoryContext";
import AutoLinkPlugin from "./plugins/AutoLinkPlugin";
import CodeActionMenuPlugin from "./plugins/CodeActionMenuPlugin";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import FloatingLinkEditorPlugin from "./plugins/FloatingLinkEditorPlugin";
import FloatingTextFormatToolbarPlugin from "./plugins/FloatingTextFormatToolbarPlugin";
import KeywordsPlugin from "./plugins/KeywordsPlugin";
import LinkPlugin from "./plugins/LinkPlugin";
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import TabFocusPlugin from "./plugins/TabFocusPlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import ContentEditable from "./ui/ContentEditable";
import Placeholder from "./ui/Placeholder";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import { EditorState, LexicalEditor } from "lexical";
import { SharedAutocompleteContext } from "./context/SharedAutocompleteContext";
import "./index.css";
import LoadHtmlPlugin from "./plugins/LoadHtmlPlugin";
import { useTextMonitoring } from "@/hooks/supervisor";
import { InsertTemplatePlugin } from "./plugins/InsertTemplatePlugin";
import { OnPastePlugin } from "./plugins/OnPastePlugin";
import ImagesPlugin from "./plugins/ImagesPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ToolbarContext } from "./context/ToolbarContext";
import { TotalAttachmentSizeLimit, AttachmentSizeLimit } from "@/common/constants";

export interface EditorProps {
	initialHtml: string;
	onChange: (html: string) => void;
	additionalBar?: (editor?: LexicalEditor | undefined) => JSX.Element;
	afterContent?: JSX.Element;
	readOnly?: boolean;
	currentAttachmentsSizeBytes?: number;
	maxTotalAttachmentsSizeBytes?: number;
	maxAttachmentSizeBytes?: number;
	onPaste?: (pastedHtml: string, pastedText: string) => string | null;
	onPastedFiles?: (files: File[]) => void;
}

export const Editor = (props: EditorProps) => {
	const {
		additionalBar,
		afterContent,
		onChange,
		initialHtml,
		readOnly,
		currentAttachmentsSizeBytes = 0,
		maxTotalAttachmentsSizeBytes = TotalAttachmentSizeLimit,
		maxAttachmentSizeBytes = AttachmentSizeLimit,
		onPaste,
		onPastedFiles,
	} = props;
	const { historyState } = useSharedHistoryContext();
	const [floatingAnchorElem, setFloatingAnchorElem] =
		useState<HTMLDivElement | null>(null);
	const [isSmallWidthViewport, setIsSmallWidthViewport] =
		useState<boolean>(false);
	const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);
	const [shouldLoadInitial, setShouldLoadInitial] = useState<boolean>(true);
	const [editor] = useLexicalComposerContext();
	const [activeEditor, setActiveEditor] = useState(editor);
	const editorRef = useRef<LexicalEditor | undefined>(editor);

	const persistEditorContent = (lexicalEditor: LexicalEditor) => {
		if (!shouldLoadInitial) {
			lexicalEditor.update(() =>
				onChange(
					$generateHtmlFromNodes(lexicalEditor).replace(/class=".+?"/g, "")
				)
			);
		}
		setShouldLoadInitial(false);
	};

	const onRef = (_floatingAnchorElem: HTMLDivElement) => {
		if (_floatingAnchorElem !== null) {
			setFloatingAnchorElem(_floatingAnchorElem);
		}
	};

	const isTextMonitoring = useTextMonitoring();

	useEffect(() => {
		activeEditor.setEditable(!readOnly);
		const updateViewPortWidth = () => {
			const isNextSmallWidthViewport =
				CAN_USE_DOM && window.matchMedia("(max-width: 1025px)").matches;

			if (isNextSmallWidthViewport !== isSmallWidthViewport) {
				setIsSmallWidthViewport(isNextSmallWidthViewport);
			}
		};
		updateViewPortWidth();
		window.addEventListener("resize", updateViewPortWidth);

		return () => {
			window.removeEventListener("resize", updateViewPortWidth);
		};
	}, [isSmallWidthViewport]);

	return (
		<SharedHistoryContext>
			<SharedAutocompleteContext>
				<ToolbarContext>
					<div className="editor-shell">
						<div className="editor-container">
							<AutoFocusPlugin />
							<ClearEditorPlugin />
							<HashtagPlugin />
							<KeywordsPlugin />
							<AutoLinkPlugin />
							<HistoryPlugin externalHistoryState={historyState} />
							<RichTextPlugin
								contentEditable={
									<div className="editor-scroller">
										<div className="editor" ref={onRef}>
											<ContentEditable />
										</div>
									</div>
								}
								placeholder={
									<Placeholder>{"Type your message..."}</Placeholder>
								}
								ErrorBoundary={LexicalErrorBoundary}
							/>
							{afterContent}
							<CodeHighlightPlugin />
							<ListPlugin />
							<ListMaxIndentLevelPlugin maxDepth={7} />
							<ImagesPlugin
								captionsEnabled={false}
							/>
							<LinkPlugin />
							<HorizontalRulePlugin />
							<TabFocusPlugin />
							<TabIndentationPlugin />
							{floatingAnchorElem && !isSmallWidthViewport && (
								<>
									<CodeActionMenuPlugin anchorElem={floatingAnchorElem} />
									<FloatingLinkEditorPlugin
										anchorElem={floatingAnchorElem}
										isLinkEditMode={isLinkEditMode}
										setIsLinkEditMode={setIsLinkEditMode}
									/>
									{!isTextMonitoring && (
										<FloatingTextFormatToolbarPlugin
											anchorElem={floatingAnchorElem}
										/>
									)}
								</>
							)}
						</div>
						<LoadHtmlPlugin
							htmlContent={initialHtml}
							shouldLoad={isTextMonitoring ? true : shouldLoadInitial}
						/>
						<ToolbarPlugin
							editor={editor}
							activeEditor={activeEditor}
							setActiveEditor={setActiveEditor}
							setIsLinkEditMode={setIsLinkEditMode}
							isEmailMonitoring={isTextMonitoring}
							currentAttachmentsSizeBytes={currentAttachmentsSizeBytes}
							maxTotalAttachmentsSizeBytes={maxTotalAttachmentsSizeBytes}
							maxAttachmentSizeBytes={maxAttachmentSizeBytes}
						/>
						<EditorRefPlugin editorRef={editorRef} />
						<OnChangePlugin
							onChange={(_state: EditorState, editor: LexicalEditor) => {
								persistEditorContent(editor);
							}}
						/>
						<InsertTemplatePlugin />
						{onPaste && (
							<OnPastePlugin
								onPaste={onPaste}
								onAfterPaste={persistEditorContent}
								onPastedFiles={onPastedFiles}
							/>
						)}
						{!!additionalBar && additionalBar(editorRef.current)}
					</div>
				</ToolbarContext>
			</SharedAutocompleteContext>
		</SharedHistoryContext>
	);
};
